import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { changeAddressContract, getContract } from '../../../../services/contracts.service';
import { AxiosError } from 'axios';
import { showAxiosMessageError } from '../../../../services/error-message.service';
import { ContractDto } from '../../../../services/dtos/contract.dto';
import { useSnackbars } from '../../../../hooks/snackbar.hook';
import AddressContractForm from '../../../../components/forms/address-contract/address-contract.form';
import { AddressContractFormDto } from '../../../../services/dtos/form/address-contract-form.dto';
import { getPageContent } from '../../../../services/page.service';
import { ReferenceDto } from '../../../../services/dtos/reference.dto';
import { AddressContractDto } from '../../../../services/dtos/address-contract.dto';
import { getAddressGouv } from '../../../../services/address.service';
import { AddressGouvFeaturesPropertiesFormDto } from '../../../../services/dtos/form/address-gouv-features-properties-form.dto';

/**
 * Page Changement d'adresse
 * @constructor
 */
export default function AddressContractPage() {
    //navigate
    const navigate = useNavigate();
    //SnackBar pour les messages
    const showMessage = useSnackbars();
    //Permet de savoir si on est passé une fois dans l'useEffect
    const dataFetchedRef = useRef(false);
    //Params
    const { id } = useParams();
    //Ajout des useState
    const [addressContract, setAddress] = useState<string | undefined>();
    const [validForm, setValidForm] = useState(true);
    const [countries, setCountries] = useState<ReferenceDto[]>([{ value: 'France', label: 'France' }]);
    const [country, setSelectedCountry] = useState<ReferenceDto>({ value: 'France', label: 'France' });
    const [addressesGouv, setAddressesGouv] = useState<AddressGouvFeaturesPropertiesFormDto[]>([]);

    const minSizeSearch = 3;
    const handleChangeCountry = (e: React.SyntheticEvent, v: ReferenceDto | null) => {
        if (v) {
            setSelectedCountry(v);
            // Réactiver le formulaire si un pays est sélectionné
            if (!validForm && v.value) {
                setValidForm(true);
            }
        } else {
            setSelectedCountry({ value: '', label: '' });
            setValidForm(false);
        }
    };

    // Handler pour la recherche d'adresse via API data.gouv
    const handleSearchAddress = (event: object, value: string, reason: string) => {
        // On lance la recherche sinon on ne fait rien
        if (reason === 'input' && value?.trimEnd().length >= minSizeSearch) {
            getAddressGouv(value)
                .then(result => {
                    if (result) {
                        setAddressesGouv(result.features.map(feature => feature.properties));
                    }
                })
                .catch((error: Error | AxiosError) => {
                    showAxiosMessageError(error, showMessage);
                });
        }
    };

    // Handler pour la sélection d'une adresse
    const handleSelectAddress = (address: AddressGouvFeaturesPropertiesFormDto | null) => {
        // Optionnel : on peut faire quelque chose quand une adresse est sélectionnée
        if (address) {
            console.log('Adresse sélectionnée:', address);
        }
    };

    //Use effect
    useEffect(() => {
        if (dataFetchedRef.current) {
            return;
        }
        dataFetchedRef.current = true;
        //Récupération du contrat pour afficher l'adresse
        getContract(id)
            .then((contract: ContractDto) => {
                setAddress(contract?.informations?.payeur?.adresse);
            })
            .catch((error: Error | AxiosError) => {
                showAxiosMessageError(error, showMessage);
            });

        //Récupération des pays à afficher dans le select
        getPageContent('adresse')
            .then(async result => {
                if (result) {
                    let countriesArray: ReferenceDto[] = [];
                    JSON.parse(result[0].valeur, (key, value: string) => {
                        if (key !== null && key !== undefined && key !== '') {
                            countriesArray.push({ value: value, label: value });
                        }
                    });
                    setCountries(countriesArray);
                }
            })
            .catch(error => {
                console.error('Erreur lors de la récupération du contenu de la page');
                showAxiosMessageError(error, showMessage);
            });
    }, [showMessage, id]);

    //Handler utilisé lors du submit du formulaire
    const handleSumbitForm = (values: AddressContractFormDto) => {
        setValidForm(false);

        // Vérification que le pays est sélectionné
        if (!country.value) {
            showMessage.errorAlert('Veuillez sélectionner un pays');
            setValidForm(true);
            return;
        }

        const data: AddressContractDto = {
            numeroRue: values.numeroRue,
            rue: values.rue,
            ville: values.ville,
            pays: country.value,
            codePostal: parseInt(values.codePostal, 10),
            boitePostale: values.boitePostale,
            complement: {
                batiment: values.complement?.batiment,
                escalier: values.complement?.escalier,
                etage: values.complement?.etage,
                appartement: values.complement?.appartement,
                ville: values.complement?.ville,
                numeroRue: values.complement?.numeroRue,
                rue: values.complement?.rue,
            },
        };

        //Appel API pour la création du relevé de compteur
        //On ne modifie pas le DTO, il est bon pour l'envoie
        changeAddressContract(data, id)
            .then(() => {
                showMessage.successAlert("Votre changement d'adresse a bien été pris en compte.");
                navigate('/contrat/' + id);
            })
            .catch((error: Error | AxiosError) => {
                showAxiosMessageError(error, showMessage);
            })
            .finally(() => {
                setValidForm(true);
            });
    };

    //Appel de la vue qui est retournée
    return (
        <AddressContractForm
            handleSumbitForm={handleSumbitForm}
            validForm={validForm && country.value !== ''}
            addressContract={addressContract}
            id={id}
            countriesSelection={countries}
            handleChangeCountry={handleChangeCountry}
            selectedCountry={country}
            handleSearchAddress={handleSearchAddress}
            addressesGouv={addressesGouv}
            handleSelectAddress={handleSelectAddress}
        ></AddressContractForm>
    );
}
